import { create } from 'zustand';
import { receiptsAPI } from '@matix/api-client';
import type {
  CancelReason,
  ReceiptListItem,
  ReceiptListScope,
  ReceiptListType,
  ReceiptOrderTypeFilter,
} from '@matix/types';

/**
 * Receipts list store — Phase 5d.3.1.
 *
 * Replaces the simpler 5d.3 store with everything the legacy-style
 * Orders History layout needs:
 *
 *   - paging (page + limit + total)
 *   - selection state (which row's details panel is visible)
 *   - filters (scope + search + date range) plus a refresh action
 *
 * The legacy ERP exposes more filters still (payment type, status);
 * add them here when a cashier needs them on a regular basis.
 */

const PAGE_LIMIT_DEFAULT = 20;

/**
 * Today, as the `YYYY-MM-DD` a date input speaks.
 *
 * Built from the tablet's LOCAL calendar fields, not from `toISOString()`.
 * The till stands in the shop, so its local day and the tenant's day are the
 * same day — while the ISO string is UTC, which for a UTC+3 tenant is still
 * yesterday until 3am and would open the screen on the wrong date every early
 * morning.
 */
export function todayLocalISO(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** How far back the receipts list opens: the last 60 days, today included. */
export const DEFAULT_RANGE_DAYS = 60;

/**
 * The local calendar day `days` days before `now`, as `YYYY-MM-DD`.
 *
 * Stepped on the local calendar, not by subtracting milliseconds, so a DST
 * change inside the window cannot land it on the wrong day.
 */
export function daysAgoLocalISO(days: number, now: Date = new Date()): string {
  return todayLocalISO(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days, 12, 0, 0));
}

interface ReceiptsState {
  rows: ReceiptListItem[];
  total: number;
  loading: boolean;
  error: string | null;

  scope: ReceiptListScope;
  /** All / Sales / Returns — the admin Orders screen's toggle, filtered
   *  server-side so the page, the total and the export agree. */
  type: ReceiptListType;
  /** Order type (Inv_Receipt_h.OrderType 1-5, or 'none'); 'all' = every type.
   *  Server-side like the rest, so the page, the total and the export agree. */
  orderType: ReceiptOrderTypeFilter;
  /** Inv_PaymentType.id; null = every payment type. Server-side like the rest. */
  paymentTypeId: number | null;
  search: string;
  /**
   * Inclusive `YYYY-MM-DD` window, empty string = open-ended on that side.
   *
   * Defaults to the last 60 days, today included (`DEFAULT_RANGE_DAYS`) —
   * wide enough that a customer's receipt from last month is one search
   * away, while a branch with years of history behind it still runs a
   * bounded query. The reset ✕ opens it to every date. Was today-only until
   * 2026-09-22, which sent cashiers to the date pickers for most lookups.
   *
   * Kept as the exact strings the date inputs produce and sent to the server
   * verbatim. `Entry_Date` is naive tenant wall-clock, so any Date/ISO round
   * trip here would shift the window by the tablet's offset.
   */
  dateFrom: string;
  dateTo: string;
  /**
   * Time of day on each end, `HH:mm`, empty = the whole day on that end.
   *
   * Kept beside the dates rather than folded into them because the wire
   * format is the endpoint's `date_from` + `time_from` pair, and because
   * "clear dates" has to be able to clear a half-filled window. The screen
   * joins the two back into the `datetime-local` value its pickers speak.
   *
   * Defaults to the whole day, so the screen opens on exactly the window it
   * opened on before there was a clock here at all.
   */
  timeFrom: string;
  timeTo: string;
  /** True until the cashier touches the dates: the window is still the
   *  default "last 60 days to today" and may be rolled forward to a new today. */
  rangeIsDefault: boolean;
  hasOpenShift: boolean;

  /** 1-based. The endpoint accepts the same. */
  page: number;
  limit: number;

  /** id of the currently-selected row (null = nothing selected). */
  selectedId: number | null;

  initialized: boolean;

  setScope: (scope: ReceiptListScope) => void;
  setType: (type: ReceiptListType) => void;
  setOrderType: (orderType: ReceiptOrderTypeFilter) => void;
  setPaymentTypeId: (paymentTypeId: number | null) => void;
  setSearch: (search: string) => void;
  /** The filtered window: a date on each end, and a time of day on it
   *  (`''` = the whole day on that end). */
  setDateTimeRange: (
    from: string,
    to: string,
    fromTime: string,
    toTime: string,
  ) => void;
  clearDateRange: () => void;
  /** The Refresh button: re-fetch now, rolling an untouched default window
   *  forward to today first (a till left open overnight). */
  refreshNow: () => Promise<void>;
  setPage: (page: number) => void;
  selectRow: (id: number | null) => void;
  refresh: () => Promise<void>;
  /**
   * Void a receipt, then reload the page so the row comes back marked.
   *
   * Resolves to a message for the toast and throws nothing — the screen
   * shows what happened either way. The row is NOT patched optimistically:
   * a void changes `can_edit`, `can_void` and the shift totals behind it, and
   * a refresh is one query the cashier is already waiting on.
   */
  void: (
    id: number,
    cancelReasonId: number | null,
    note: string | null,
  ) => Promise<{ ok: boolean; message: string }>;

  /**
   * The tenant's cancellation reasons — the void dialog's picker.
   *
   * Loaded ONCE per session and kept: it is a handful of ERP rows that change
   * about never, and the dialog opens on a cashier who is already mid-void.
   * `cancelReasonsLoaded` is separate from a non-empty list because an EMPTY
   * list is a real answer on two tenants, and the dialog has to tell "not
   * fetched yet" from "this tenant classifies nothing".
   */
  cancelReasons: CancelReason[];
  cancelReasonsLoaded: boolean;
  loadCancelReasons: () => Promise<void>;
  /**
   * Every receipt the CURRENT filters match, not just the page on screen —
   * what "Export to Excel" puts in the workbook.
   *
   * Paged server-side at `EXPORT_PAGE_SIZE` and stopped at `EXPORT_MAX_ROWS`,
   * with `truncated` saying which happened. A cashier who exports an
   * unbounded window (dates cleared, a year of history behind the branch)
   * gets the most recent rows and a warning rather than a tab that hangs
   * pulling a hundred thousand of them through a shop's uplink.
   */
  fetchAllForExport: () => Promise<{ rows: ReceiptListItem[]; truncated: boolean }>;
}

/**
 * The export's page size and ceiling. 500 is the endpoint's own comfort zone
 * (its cap is 1000) and 20 pages of it covers a busy branch's whole month;
 * past that the filter is the thing to narrow, not the fetch.
 */
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 10_000;

export const useReceiptsStore = create<ReceiptsState>((set, get) => ({
  rows: [],
  total: 0,
  loading: false,
  error: null,
  // "All", not the open shift. A cashier looking something up is as often
  // after a colleague's receipt from earlier in the day as their own, and the
  // shift scope hid those behind a tab most people never pressed. The date
  // window below still keeps the query bounded.
  scope: 'all',
  type: 'all',
  orderType: 'all',
  paymentTypeId: null,
  search: '',
  dateFrom: daysAgoLocalISO(DEFAULT_RANGE_DAYS - 1),
  dateTo: todayLocalISO(),
  timeFrom: '',
  timeTo: '',
  rangeIsDefault: true,
  hasOpenShift: false,
  page: 1,
  limit: PAGE_LIMIT_DEFAULT,
  selectedId: null,
  initialized: false,

  setScope: (scope) => {
    // Switching scope resets the page (results are different).
    set({ scope, page: 1, selectedId: null });
    void get().refresh();
  },

  setType: (type) => {
    set({ type, page: 1, selectedId: null });
    void get().refresh();
  },

  setOrderType: (orderType) => {
    set({ orderType, page: 1, selectedId: null });
    void get().refresh();
  },

  setPaymentTypeId: (paymentTypeId) => {
    set({ paymentTypeId, page: 1, selectedId: null });
    void get().refresh();
  },

  setSearch: (search) => {
    // Search resets paging too. Debouncing happens in the screen.
    set({ search, page: 1 });
  },

  setDateTimeRange: (from, to, fromTime, toTime) => {
    // A different window is a different result set, so page 1 and no stale
    // selection pointing at a row that is no longer in the list.
    // A time with no date on that end is meaningless — drop it rather than
    // send it, so a cleared end stays genuinely open.
    set({
      dateFrom: from,
      dateTo: to,
      timeFrom: from ? fromTime : '',
      timeTo: to ? toTime : '',
      rangeIsDefault: false,
      page: 1,
      selectedId: null,
    });
    // Not debounced: a date input fires once, when the picker closes or the
    // field is complete — unlike the search box, which fires per keystroke.
    void get().refresh();
  },

  clearDateRange: () => {
    // Clears to open-ended, NOT back to today — "clear" that silently
    // reapplies a filter is the kind of control people stop trusting. The
    // times go with the dates they belonged to.
    set({ dateFrom: '', dateTo: '', timeFrom: '', timeTo: '', rangeIsDefault: false, page: 1, selectedId: null });
    void get().refresh();
  },

  refreshNow: async () => {
    const today = todayLocalISO();
    if (get().rangeIsDefault && get().dateTo !== today) {
      set({ dateFrom: daysAgoLocalISO(DEFAULT_RANGE_DAYS - 1), dateTo: today, page: 1, selectedId: null });
    }
    await get().refresh();
  },

  setPage: (page) => {
    if (page < 1) page = 1;
    set({ page, selectedId: null });
    void get().refresh();
  },

  selectRow: (id) => set({ selectedId: id }),

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const { page, limit } = get();
      const response = await receiptsAPI.list({ ...filterParams(get()), page, limit });
      set({
        rows: response.data,
        total: response.total,
        hasOpenShift: response.has_open_shift,
        loading: false,
        initialized: true,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load receipts';
      set({ loading: false, error: message, initialized: true });
    }
  },

  cancelReasons: [],
  cancelReasonsLoaded: false,

  loadCancelReasons: async () => {
    if (get().cancelReasonsLoaded) return;
    try {
      const data = await receiptsAPI.cancelReasons();
      set({ cancelReasons: data, cancelReasonsLoaded: true });
    } catch {
      // A lookup list that fails to load must not be the reason a receipt
      // cannot be voided. `loaded` stays false so the next open retries, and
      // the dialog meanwhile behaves like a tenant with no reasons: note only.
      set({ cancelReasons: [] });
    }
  },

  void: async (id, cancelReasonId, note) => {
    try {
      const result = await receiptsAPI.void(id, { cancel_reason_id: cancelReasonId, note });
      // Refresh before returning, so the caller's toast and the marked row
      // land together rather than the message arriving first.
      await get().refresh();
      return {
        ok: true,
        message: result.voided_now
          ? `Order #${result.entry_no} voided`
          : `Order #${result.entry_no} was already voided`,
      };
    } catch (err) {
      // The API's refusals are the interesting ones — "voucher already
      // posted", "not in your current shift" — and they arrive as the error
      // envelope's `message`. Surface it verbatim rather than a generic line;
      // it is the only thing that tells the cashier what to do instead.
      return { ok: false, message: voidErrorMessage(err) };
    }
  },

  fetchAllForExport: async () => {
    const filters = filterParams(get());
    const rows: ReceiptListItem[] = [];
    let truncated = false;
    for (let page = 1; ; page += 1) {
      const response = await receiptsAPI.list({ ...filters, page, limit: EXPORT_PAGE_SIZE });
      rows.push(...response.data);
      // Stop on a short page (the last one) or on the server's own total —
      // never on `data.length === 0` alone, which would loop forever against
      // a build that answers an over-run page with the last one.
      if (response.data.length < EXPORT_PAGE_SIZE || rows.length >= response.total) break;
      if (rows.length >= EXPORT_MAX_ROWS) {
        truncated = true;
        break;
      }
    }
    return { rows, truncated };
  },
}));

/**
 * The filter half of a list request — scope, type, search and the date/time
 * window — shared by the on-screen page and the export so the workbook can
 * never hold a different set of receipts from the table it was exported from.
 */
function filterParams(s: ReceiptsState): {
  scope: ReceiptListScope;
  type: ReceiptListType;
  order_type?: ReceiptOrderTypeFilter;
  payment_type_id?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  time_from?: string;
  time_to?: string;
} {
  return {
    scope: s.scope,
    type: s.type,
    // Omitted when 'all', so the request is exactly what it was before.
    order_type: s.orderType === 'all' ? undefined : s.orderType,
    payment_type_id: s.paymentTypeId ?? undefined,
    search: s.search.trim() || undefined,
    date_from: s.dateFrom || undefined,
    date_to: s.dateTo || undefined,
    time_from: (s.dateFrom && s.timeFrom) || undefined,
    time_to: (s.dateTo && s.timeTo) || undefined,
  };
}

/** `{ error, message }` from pos-api, unwrapped through axios. */
function voidErrorMessage(err: unknown): string {
  const body = (err as { response?: { data?: { message?: string; error?: string } } })
    ?.response?.data;
  if (body?.message) return body.message;
  if (body?.error) return body.error;
  return err instanceof Error ? err.message : 'Could not void this receipt';
}
